/*
 * Settings -> Multi-factor authentication.
 *
 * The signed-in user's own second factors — not the tenant's MFA provider,
 * which is the other MFA card on the Settings home and a different thing
 * entirely. One row per factor the tenant offers, Add if this user has not
 * registered it, Update if they have.
 *
 * `mfa/status` answers three separate questions that are easy to collapse into
 * one:
 *   status       the TENANT offers this factor
 *   enrolled     THIS USER has registered it
 *   mfa_enabled  the tenant has at least one second factor configured — it is
 *                not "this user has MFA", and must not be read as such
 *
 * The email travels in the request body by the service's design, and the
 * endpoint is session-gated but not session-bound: it answers for whatever
 * address it is given. It is therefore always USER_EMAIL(), never a value from
 * the route, a query string, or a form. (Flagged upstream for a follow-up
 * ticket; until it is session-bound, this rule is the only thing keeping the
 * page from reading someone else's factors.)
 *
 * Add / Remove asymmetry is deliberate: there is no un-enrol control here, so
 * a user cannot strip their last factor and lock themselves out.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { message, Spin } from "antd";
import { ShieldCheck } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { CountChip, EmbeddedShell, PageHeader, PageShell, TableCard } from "../../../design-system/tableUi";
import { USER_EMAIL } from "../../../constants";
import { getDomainUrl } from "../../../utils/helpers";
import { logOutUser } from "../../../common";
import { dispatchGetMfaStatus } from "../../../utils/apiservice";
import { btn, Callout, factorMeta, fmtDay } from "./mfaCommon";
import TotpSetupDialog from "./TotpSetupDialog";
import PasskeySetupDialog from "./PasskeySetupDialog";
import SelfEnrollModal from "../../enrollment/SelfEnrollModal";

function FactorRow({ row, first, onSetup }) {
  const { meta, display_name, enrolled, enrolled_at, is_default } = row;
  const IconTag = meta.icon;
  const added = enrolled ? fmtDay(enrolled_at) : null;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "15px 18px",
      borderTop: first ? "none" : `1px solid ${AN_COLORS.line2}`,
    }}>
      <span style={{
        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
        background: enrolled ? "var(--an-successBg)" : AN_COLORS.tint,
        color: enrolled ? AN_COLORS.success : AN_COLORS.ink3,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <IconTag size={18} strokeWidth={1.8} />
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Shown from display_name — tenant-editable, which is exactly why
              every behavioural branch on this page keys off `name` instead. */}
          <span style={{ fontSize: 13.5, fontWeight: 600, color: AN_COLORS.ink }}>
            {display_name || meta.title}
          </span>
          {is_default && (
            <span style={{
              fontFamily: AN_MONO, fontSize: 9.5, letterSpacing: 0.8, textTransform: "uppercase",
              padding: "2px 6px", borderRadius: 4, color: AN_COLORS.primary,
              background: AN_COLORS.primaryL, border: "1px solid var(--an-infoBd)",
            }}>
              Default
            </span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 3 }}>
          {enrolled ? (added ? `Added ${added}` : "Added") : meta.blurb}
        </div>
      </div>

      <button type="button" style={btn(enrolled ? "secondary" : "primary")}
        onClick={() => onSetup(meta.key, enrolled ? "update" : "add")}>
        {enrolled ? (meta.updateLabel || "Update") : "Add"}
      </button>
    </div>
  );
}

/*
 * `embedded` renders the screen for a host that already supplies the title —
 * its only mount today is the first panel of Tenant Config. The email is still
 * USER_EMAIL() in both modes: there is deliberately no `email` prop, because a
 * host-supplied address would defeat the rule in the header comment above.
 */
export default function MfaFactorsPage({ embedded = false }) {
  const email = USER_EMAIL();
  const url = getDomainUrl();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // { factor: "totp" | "passkey" | "push", mode: "add" | "update" }
  const [dialog, setDialog] = useState(null);

  const load = useCallback(() => {
    if (!email || !url) {
      setLoading(false);
      setError("Your session is missing an account or tenant address. Sign in again to manage your factors.");
      return;
    }
    setLoading(true);
    setError("");
    const failed = (msg) => { setLoading(false); setError(msg); };
    dispatchGetMfaStatus({
      payload: { email, url },
      onSuccess: (res) => { setLoading(false); setStatus(res); },
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { setLoading(false); logOutUser(); return; }
        failed(m || "Could not load your MFA factors.");
      },
      onError: ({ message: m }) => failed(m || "Could not load your MFA factors."),
    });
  }, [email, url]);

  useEffect(() => { load(); }, [load]);

  // Push set-up finishes on the phone, so the QR dialog has no "done" event of
  // its own. While it is open for a first phone, re-read mfa/status quietly
  // and close it once the service reports Push as enrolled.
  const pushWaiting = dialog?.factor === "push" && dialog?.mode === "add";
  useEffect(() => {
    if (!pushWaiting || !email || !url) return undefined;
    let live = true;
    const id = setInterval(() => {
      dispatchGetMfaStatus({
        payload: { email, url },
        onSuccess: (res) => {
          if (!live) return;
          const push = (res?.configured_methods || []).find((m) => factorMeta(m?.name)?.key === "push");
          if (push?.enrolled) {
            setStatus(res);
            setDialog(null);
            message.success("AuthNull Authenticator is set up. Your next sign-in can use a push.");
          }
        },
        onFailure: () => {},
        onError: () => {},
      });
    }, 4000);
    return () => { live = false; clearInterval(id); };
  }, [pushWaiting, email, url]);

  // Rows the tenant offers AND this page can set up. A factor with no dialog
  // behind it (SMS, the legacy `webauthn` entry) is dropped rather than
  // rendered with a button that leads nowhere.
  const rows = useMemo(() => (
    (status?.configured_methods || [])
      .filter((m) => String(m?.status || "").toLowerCase() === "active")
      .map((m) => ({ ...m, meta: factorMeta(m?.name) }))
      .filter((m) => m.meta)
  ), [status]);

  const enrolledCount = rows.filter((r) => r.enrolled).length;

  const Shell = embedded ? EmbeddedShell : PageShell;
  const chip = !loading && !error && rows.length
    ? <CountChip count={enrolledCount} label={enrolledCount === 1 ? "factor enrolled" : "factors enrolled"} />
    : null;

  // Re-read from the service rather than patching the row locally: the upsert
  // also moves `is_default` and `enrolled_at`, and a hand-patched row would
  // disagree with what the next sign-in actually offers.
  const onEnrolled = ({ message: text } = {}) => {
    setDialog(null);
    if (text) message.success(text);
    load();
  };

  return (
    <Shell>
      {embedded ? (
        // The panel header is the title here; what survives is the two facts a
        // user cannot get from it — whose factors these are, and how many are on.
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap", marginBottom: 14,
        }}>
          <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>
            Second factors on your own account{email ? ` — ${email}` : ""}.
          </span>
          {chip}
        </div>
      ) : (
        <PageHeader
          eyebrow="Settings · Authentication"
          title="Multi-factor authentication"
          subtitle={`Second factors on your own account${email ? ` — ${email}` : ""}.`}
          chips={chip}
        />
      )}

      {loading && (
        <div style={{ padding: embedded ? "26px 0" : "60px 0", textAlign: "center" }}><Spin /></div>
      )}

      {!loading && error && (
        <div style={{ maxWidth: 620 }}>
          <Callout tone="danger">{error}</Callout>
          <div style={{ marginTop: 14 }}>
            <button type="button" style={btn("secondary")} onClick={load}>Try again</button>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div style={{ maxWidth: 720, fontFamily: AN_FONT }}>
          {/* enrolled_methods, not mfa_enabled: the tenant having factors
              configured says nothing about whether this user has registered one. */}
          {!enrolledCount && rows.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Callout icon={<ShieldCheck size={15} color={AN_COLORS.primary} />}>
                You haven&rsquo;t added a second factor yet. Anything you add here is available at your
                next sign-in.
              </Callout>
            </div>
          )}

          {rows.length ? (
            <TableCard>
              {rows.map((row, i) => (
                <FactorRow
                  key={row.factor_id ?? `${row.name}-${i}`}
                  row={row}
                  first={i === 0}
                  onSetup={(factor, mode) => setDialog({ factor, mode })}
                />
              ))}
            </TableCard>
          ) : (
            <TableCard>
              <div style={{ padding: "34px 18px", textAlign: "center", fontSize: 13, color: AN_COLORS.muted }}>
                Your tenant hasn&rsquo;t enabled any factors you can set up here.
              </div>
            </TableCard>
          )}
        </div>
      )}

      <TotpSetupDialog
        open={dialog?.factor === "totp"}
        mode={dialog?.mode}
        email={email}
        url={url}
        onClose={() => setDialog(null)}
        onEnrolled={onEnrolled}
      />
      <PasskeySetupDialog
        open={dialog?.factor === "passkey"}
        mode={dialog?.mode}
        email={email}
        url={url}
        onClose={() => setDialog(null)}
        onEnrolled={onEnrolled}
      />
      {/* Re-read on close: an extra phone, or one that finished just as the
          dialog closed, should show without a page reload. */}
      <SelfEnrollModal
        open={dialog?.factor === "push"}
        onClose={() => { setDialog(null); load(); }}
      />
    </Shell>
  );
}
